import { useCallback, useEffect, useState, useMemo } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { AccessPolicy, ProjectMembership } from '@medplum/fhirtypes';

// -----------------------------------------------------------------------------
const SUPER_ADMIN_EMAILS = ['admin@example.com'];

// -----------------------------------------------------------------------------
export interface ComponentAccess {
  // LIMS Components
  canAccessLims: boolean;
  canCreateOrders: boolean;
  canEditOrders: boolean;
  canDeleteOrders: boolean;
  canManageSpecimens: boolean;
  canViewDiagnosticReports: boolean;
  canCreateDiagnosticReports: boolean;
  canReviewResults: boolean;
  canManageConsent: boolean;

  // Admin Components
  canAccessAdmin: boolean;
  canManageUsers: boolean;
  canManageAccessPolicies: boolean;
  canViewSystemStats: boolean;

  // Patient Management
  canAccessPatients: boolean;
  canCreatePatients: boolean;
  canEditPatients: boolean;
  canViewPatientHistory: boolean;
  canManageEncounters: boolean;

  // Tasks & Workflow
  canAccessTasks: boolean;
  canClaimTasks: boolean;
  canAssignTasks: boolean;
  canCompleteTasksForOthers: boolean;
  // Practitioner Management
  canAccessPractitioners: boolean;
  canViewPractitioners: boolean;
  canManagePractitioners: boolean;

  // Payment & Billing
  canAccessPayments: boolean;
  canManageChargeItems: boolean;
  canManageInvoices: boolean;
  canViewBillingAccounts: boolean;

  // General Permissions
  canViewReports: boolean;
  canExportData: boolean;
  canManageDocuments: boolean;
}

// -----------------------------------------------------------------------------
export interface UseCurrentAccessPolicyReturn {
  accessPolicy: AccessPolicy | null;
  membership: ProjectMembership | null;
  componentAccess: ComponentAccess;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// -----------------------------------------------------------------------------
const DEFAULT_COMPONENT_ACCESS: ComponentAccess = {
  // LIMS Components - default restrictive access
  canAccessLims: false,
  canCreateOrders: false,
  canEditOrders: false,
  canDeleteOrders: false,
  canManageSpecimens: false,
  canViewDiagnosticReports: false,
  canCreateDiagnosticReports: false,
  canReviewResults: false,
  canManageConsent: false,

  // Admin Components - default no admin access
  canAccessAdmin: false,
  canManageUsers: false,
  canManageAccessPolicies: false,
  canViewSystemStats: false,

  // Patient Management - basic read access
  canAccessPatients: true,
  canCreatePatients: false,
  canEditPatients: false,
  canViewPatientHistory: true,
  canManageEncounters: false,

  // Tasks & Workflow - basic task access
  canAccessTasks: true,
  canClaimTasks: true,
  canAssignTasks: false,
  canCompleteTasksForOthers: false,
  // Practitioner Management - default no access
  canAccessPractitioners: false,
  canViewPractitioners: false,
  canManagePractitioners: false,

  // Payment & Billing - default no access
  canAccessPayments: false,
  canManageChargeItems: false,
  canManageInvoices: false,
  canViewBillingAccounts: false,

  // General Permissions - basic access
  canViewReports: true,
  canExportData: false,
  canManageDocuments: false,
};

// -----------------------------------------------------------------------------
/**
 * Determines component access based on access policy rules and resource permissions
 * @param accessPolicy - The user's access policy defining resource permissions
 * @param membership - The user's project membership information
 * @param userIsAdmin - Whether the user has admin privileges
 * @returns ComponentAccess object with boolean flags for each permission
 */
const determineComponentAccess = (
  accessPolicy: AccessPolicy | null,
  membership: ProjectMembership | null,
  userIsAdmin: boolean
): ComponentAccess => {
  // Admin users get full access to everything
  if (userIsAdmin) {
    return {
      canAccessLims: true,
      canCreateOrders: true,
      canEditOrders: true,
      canDeleteOrders: true,
      canManageSpecimens: true,
      canViewDiagnosticReports: true,
      canCreateDiagnosticReports: true,
      canReviewResults: true,
      canManageConsent: true,
      canAccessAdmin: true,
      canManageUsers: true,
      canManageAccessPolicies: true,
      canViewSystemStats: true,
      canAccessPatients: true,
      canCreatePatients: true,
      canEditPatients: true,
      canViewPatientHistory: true,
      canManageEncounters: true,
      canAccessTasks: true,
      canClaimTasks: true,
      canAssignTasks: true,
      canCompleteTasksForOthers: true,
      canAccessPractitioners: true,
      canViewPractitioners: true,
      canManagePractitioners: true,
      canAccessPayments: true,
      canManageChargeItems: true,
      canManageInvoices: true,
      canViewBillingAccounts: true,
      canViewReports: true,
      canExportData: true,
      canManageDocuments: true,
    };
  }

  // Start with default restrictive permissions
  const componentAccess: ComponentAccess = { ...DEFAULT_COMPONENT_ACCESS };

  // If no access policy, return default permissions
  if (!accessPolicy?.resource) {
    return componentAccess;
  }

  // For now, we grant permissions based on resource presence in the access policy
  for (const resource of accessPolicy.resource) {
    const resourceType = resource.resourceType;

    if (resourceType === 'ServiceRequest') {
      componentAccess.canAccessLims = true;
      componentAccess.canCreateOrders = true;
      componentAccess.canEditOrders = true;
      if (userIsAdmin) {
        componentAccess.canDeleteOrders = true;
      }
    }

    if (resourceType === 'Specimen') {
      componentAccess.canManageSpecimens = true;
    }

    if (resourceType === 'DiagnosticReport') {
      componentAccess.canViewDiagnosticReports = true;
      componentAccess.canCreateDiagnosticReports = true;
      componentAccess.canReviewResults = true;
    }

    if (resourceType === 'Consent') {
      componentAccess.canManageConsent = true;
    }

    if (resourceType === 'Patient') {
      componentAccess.canAccessPatients = true;
      componentAccess.canCreatePatients = true;
      componentAccess.canEditPatients = true;
    }

    if (resourceType === 'Encounter') {
      componentAccess.canManageEncounters = true;
    }

    if (resourceType === 'Task') {
      componentAccess.canAccessTasks = true;
      componentAccess.canClaimTasks = true;
      componentAccess.canAssignTasks = true;
      componentAccess.canCompleteTasksForOthers = true;
    }

    if (resourceType === 'Practitioner') {
      componentAccess.canAccessPractitioners = true;
      componentAccess.canViewPractitioners = true;
      componentAccess.canManagePractitioners = true;
    }

    // User and admin permissions
    if (resourceType === 'User' || resourceType === 'Practitioner') {
      componentAccess.canManageUsers = true;
    }

    if (resourceType === 'AccessPolicy') {
      componentAccess.canAccessAdmin = true;
      componentAccess.canManageAccessPolicies = true;
    }

    // Document management
    if (resourceType === 'DocumentReference') {
      componentAccess.canManageDocuments = true;
    }

    // Payment & Billing permissions
    if (resourceType === 'ChargeItem') {
      componentAccess.canAccessPayments = true;
      componentAccess.canManageChargeItems = true;
    }

    if (resourceType === 'Invoice') {
      componentAccess.canAccessPayments = true;
      componentAccess.canManageInvoices = true;
    }

    if (resourceType === 'Account') {
      componentAccess.canAccessPayments = true;
      componentAccess.canViewBillingAccounts = true;
    }

    // General permissions
    if (resourceType === 'Bundle') {
      componentAccess.canExportData = true;
    }
  }

  // Additional logic based on profile or membership properties
  if (membership?.profile?.display?.toLowerCase().includes('admin')) {
    componentAccess.canAccessAdmin = true;
    componentAccess.canViewSystemStats = true;
  }

  if (membership?.profile?.display?.toLowerCase().includes('lab')) {
    componentAccess.canAccessLims = true;
    componentAccess.canManageSpecimens = true;
    componentAccess.canViewDiagnosticReports = true;
  }

  return componentAccess;
};

// -----------------------------------------------------------------------------
/**
 * Hook to fetch and manage the current user's access policy and component permissions
 * @returns Object containing access policy, membership, component permissions, and utility functions
 */
export const useCurrentAccessPolicy = (): UseCurrentAccessPolicyReturn => {
  const medplum = useMedplum();
  const profile = useMedplumProfile();

  const [accessPolicy, setAccessPolicy] = useState<AccessPolicy | null>(null);
  const [membership, setMembership] = useState<ProjectMembership | null>(null);
  const [componentAccess, setComponentAccess] = useState<ComponentAccess>(
    DEFAULT_COMPONENT_ACCESS
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // -----------------------------------------------------------------------------
  const loadAccessPolicy = useCallback(async () => {
    if (!profile) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get current login information
      const activeLogin = medplum.getActiveLogin();
      if (!activeLogin) {
        throw new Error('No active login found');
      }

      const currentMembership = medplum.getProjectMembership() || null;
      setMembership(currentMembership);

      let userAccessPolicy: AccessPolicy | null = null;

      try {
        userAccessPolicy = (await medplum.getAccessPolicy()) || null;
        setAccessPolicy(userAccessPolicy);
      } catch (accessPolicyError) {
        console.warn('Failed to load access policy:', accessPolicyError);
        // Continue without access policy - user will get default permissions
      }

      // Determine component access based on access policy and membership
      const userIsAdmin =
        currentMembership?.admin === true ||
        SUPER_ADMIN_EMAILS.includes(currentMembership?.user?.display || '');
      const calculatedAccess = determineComponentAccess(
        userAccessPolicy,
        currentMembership,
        userIsAdmin
      );

      setComponentAccess(calculatedAccess);
    } catch (err) {
      console.error('Error loading access policy:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load access policy';
      setError(errorMessage);

      // Reset to default permissions on error
      setAccessPolicy(null);
      setMembership(null);
      setComponentAccess(DEFAULT_COMPONENT_ACCESS);
    } finally {
      setLoading(false);
    }
  }, [medplum, profile]);

  // -----------------------------------------------------------------------------
  useEffect(() => {
    loadAccessPolicy();
  }, [loadAccessPolicy]);
  // Memoize componentAccess to prevent unnecessary re-renders
  const memoizedComponentAccess = useMemo(
    () => componentAccess,
    [componentAccess]
  );

  // Memoize isAdmin to prevent unnecessary re-renders
  const memoizedIsAdmin = useMemo(
    () => membership?.admin === true,
    [membership?.admin]
  );

  // -----------------------------------------------------------------------------
  return {
    accessPolicy,
    membership,
    componentAccess: memoizedComponentAccess,
    isAdmin: memoizedIsAdmin,
    loading,
    error,
    refetch: loadAccessPolicy,
  };
};
